/*
 * Client-side loaders for the analytics tags. Previously these were rendered
 * inline in the SSR <head> (see Shell.tsx) and therefore ran on every page load
 * BEFORE the user could consent. They now load from here, only after the
 * "analytics" consent category has been granted (see thirdparties-ads/entry.ts).
 *
 * Each loader is idempotent — calling it twice is a no-op — so re-applying
 * consent never injects a tag twice.
 */

import { cloudflareAnalyticsId, googleTagManagerId } from "./config.ts";

let analyticsLoaded = false;

/** Load Google Tag Manager + Cloudflare analytics (once). */
export function loadAnalytics(): void {
  if (typeof document === "undefined" || analyticsLoaded) {
    return;
  }
  analyticsLoaded = true;
  loadGoogleTagManager();
  loadCloudflareAnalytics();
}

function loadGoogleTagManager(): void {
  const id = googleTagManagerId;
  if (!id || id === "0") {
    return;
  }
  // This is Google's standard GTM container snippet (gtm.js), injected
  // imperatively so it runs only after analytics consent — never inline in the
  // page <head>. Works for a container id (GTM-XXXXXXX).
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer ?? [];
  w.dataLayer.push({ "gtm.start": Date.now(), "event": "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
  document.head.appendChild(script);
}

function loadCloudflareAnalytics(): void {
  const id = cloudflareAnalyticsId;
  if (!id || id === "0") {
    return;
  }
  const beacon = document.createElement("script");
  beacon.src = "https://static.cloudflareinsights.com/beacon.min.js";
  beacon.defer = true;
  beacon.setAttribute("data-cf-beacon", JSON.stringify({ token: id }));
  document.head.appendChild(beacon);
}
